import type { TaskResponse } from '@erp/rpc';

export interface InvoiceTaskGroup {
  task: TaskResponse;
  /** Tasks nested under this one, in creation order. */
  subtasks: TaskResponse[];
  /** How many subtasks reached `completed`. */
  completedSubtasks: number;
}

/**
 * Arrange the tasks anchored to an invoice as parents with their subtasks,
 * both in creation order, so a checklist reads the way it was laid out.
 * A task whose parent is not in the list (anchored elsewhere, or beyond the
 * page) is listed at the top level; deeper descendants attach to the nearest
 * listed ancestor.
 */
export function groupInvoiceTasks(
  tasks: readonly TaskResponse[],
): InvoiceTaskGroup[] {
  const byCreation = [...tasks].sort(
    (a, b) => a.createdAt.getTime() - b.createdAt.getTime(),
  );
  const byId = new Map(byCreation.map((task) => [task.id, task]));
  const groups = new Map<string, InvoiceTaskGroup>();

  for (const task of byCreation) {
    const root = rootOf(task, byId);
    if (root.id === task.id) {
      groups.set(task.id, { task, subtasks: [], completedSubtasks: 0 });
    }
  }
  for (const task of byCreation) {
    const root = rootOf(task, byId);
    if (root.id === task.id) continue;
    const group = groups.get(root.id);
    if (!group) continue;
    group.subtasks.push(task);
    if (task.status === 'completed') group.completedSubtasks += 1;
  }
  return [...groups.values()];
}

function rootOf(
  task: TaskResponse,
  byId: ReadonlyMap<string, TaskResponse>,
): TaskResponse {
  const visited = new Set<string>([task.id]);
  let current = task;
  while (current.parentTaskId !== null) {
    const parent = byId.get(current.parentTaskId);
    if (!parent || visited.has(parent.id)) break;
    visited.add(parent.id);
    current = parent;
  }
  return current;
}
